import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutGrid, ListMusic, Monitor, Settings } from 'lucide-react';
import { haptics } from '../../utils/haptics';
import { useApp } from '../../contexts/AppContext';
import { DELIVERY_ACTIVE } from '../../utils/playlistDelivery';

/**
 * Bottom Navigation Component
 * Thumb-friendly navigation with glassmorphism and spring animations
 */
export default function BottomNavigation() {
  const location = useLocation();
  const { state } = useApp();
  const delivering = DELIVERY_ACTIVE.includes(state.playlist.delivery.status);

  const tabs = [
    { 
      name: 'Designs', 
      path: '/dashboard', 
      Icon: LayoutGrid,
    },
    {
      name: 'Service Playlist',
      shortName: 'Playlist',
      path: '/playlist',
      Icon: ListMusic,
      testId: 'playlist-tab',
      busy: delivering,
    },
    {
      name: 'Media Stations',
      path: '/media-stations',
      Icon: Monitor,
    },
    { 
      name: 'Settings', 
      path: '/settings', 
      Icon: Settings,
    },
  ];

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-30 glass border-t glass-border"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.5rem)' }}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-around h-16 relative">
          {tabs.map((tab) => {
            const isActive = location.pathname === tab.path;
            const Icon = tab.Icon;

            return (
              <NavLink
                key={tab.path}
                to={tab.path}
                onClick={() => haptics.light()}
                data-testid={tab.testId}
                className="relative flex flex-col items-center justify-center flex-1 touch-target"
              >
                {/* Active indicator background */}
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-primary-500/10 dark:bg-primary-500/20 rounded-full"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                  />
                )}

                {/* Icon */}
                <motion.div
                  animate={{
                    scale: isActive ? 1.1 : 1,
                    y: isActive ? -2 : 0,
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                  className="relative z-10"
                >
                  <Icon 
                    className={`w-6 h-6 ${
                      isActive 
                        ? 'text-primary-600 dark:text-primary-400' 
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                    strokeWidth={isActive ? 2.5 : 2}
                  />
                  {tab.busy && (
                    <span
                      className="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-primary-500 animate-pulse ring-2 ring-white dark:ring-elevated-dark-1"
                      data-testid="playlist-tab-delivering"
                      aria-hidden="true"
                    />
                  )}
                </motion.div>

                {/* Label */}
                <motion.span
                  animate={{
                    opacity: isActive ? 1 : 0.7,
                    fontWeight: isActive ? 600 : 400,
                  }}
                  className={`text-caption mt-1 relative z-10 ${
                    isActive 
                      ? 'text-primary-600 dark:text-primary-400' 
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {tab.shortName ? (
                    <>
                      <span className="min-[400px]:hidden">{tab.shortName}</span>
                      <span className="hidden min-[400px]:inline">{tab.name}</span>
                    </>
                  ) : tab.name}
                </motion.span>
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
